import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Star, Heart, Sparkles, Eye } from 'lucide-react';
import { PlatformIcon, PlatformColor, PlatformLabel } from '../common/GameCard';
import { fetchHomeGames, fetchHomeGamesByType } from '../../redux/slice/home.slice';
import { toggleWishlist } from '../../redux/slice/user.slice';
import { getGamePath } from '../../utils/gamePath';
import toast from 'react-hot-toast';

// ── Helper: Format Game Title into display prefix & highlight ──
const formatGameTitle = (title = '') => {
  if (!title) return { prefix: 'FEATURED', highlight: 'GAME' };
  if (title.includes(':')) {
    const parts = title.split(':');
    return {
      prefix: (parts[0] + ':').toUpperCase(),
      highlight: parts.slice(1).join(':').trim().toUpperCase(),
    };
  }
  const words = title.trim().split(' ');
  if (words.length > 1) {
    const half = Math.ceil(words.length / 2);
    return {
      prefix: words.slice(0, half).join(' ').toUpperCase(),
      highlight: words.slice(half).join(' ').toUpperCase(),
    };
  }
  return {
    prefix: title.toUpperCase(),
    highlight: '',
  };
};

const getGamePrice = (game) => {
  if (typeof game?.price === 'number') return `$${game.price.toFixed(2)}`;
  if (typeof game?.price === 'string' && !isNaN(parseFloat(game.price))) return `$${parseFloat(game.price).toFixed(2)}`;
  if (game?.platforms?.windows?.price) return `$${parseFloat(game.platforms.windows.price).toFixed(2)}`;
  if (game?.platforms?.playstation?.price) return `$${parseFloat(game.platforms.playstation.price).toFixed(2)}`;
  if (game?.platforms?.xbox?.price) return `$${parseFloat(game.platforms.xbox.price).toFixed(2)}`;
  return '$59.99';
};

const HERO_PLATFORM_COLORS = {
  windows: '#38bdf8',
  playstation: '#60a5fa',
  xbox: '#22c55e',
  nintendo: '#ef4444',
  quest: '#c084fc',
  android: '#3ddc84',
  ios: '#e2e8f0',
  visionpro: '#f472b6',
  steamdeck: '#00f0ff',
};

const getAvailablePlatforms = (game) => {
  if (!game) return ['windows'];

  // 1. If game.platforms is an object with available boolean (standard MongoDB model)
  if (game.platforms && typeof game.platforms === 'object' && !Array.isArray(game.platforms)) {
    const available = Object.keys(game.platforms).filter((p) => {
      const platInfo = game.platforms[p];
      return Boolean(platInfo?.available) || (typeof platInfo?.price === 'number' && platInfo.price > 0);
    });
    const valid = available.filter((p) => PlatformIcon[p]);
    if (valid.length > 0) return valid;
  }

  // 2. If game.platforms is an array of strings
  if (Array.isArray(game.platforms) && game.platforms.length > 0) {
    const valid = game.platforms
      .map((p) => String(p).toLowerCase().trim())
      .filter((p) => PlatformIcon[p]);
    if (valid.length > 0) return valid;
  }

  return ['windows'];
};


export default function Hero() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { newRelease, trending, topSelling, mostPlayed, upcoming, loading: homeLoading } = useSelector((state) => state.home || {});

  // Fetch home games if not loaded
  useEffect(() => {
    dispatch(fetchHomeGames());
  }, [dispatch]);

  // Dynamic games from database
  const cards = useMemo(() => {
    const pool = [
      ...(newRelease || []),
      ...(trending || []),
      ...(topSelling || []),
      ...(mostPlayed || []),
      ...(upcoming || []),
    ];
    const unique = [];
    const seen = new Set();

    for (const g of pool) {
      const id = g._id || g.id;
      if (id && !seen.has(id)) {
        seen.add(id);
        const img =
          g.cover_image?.url ||
          g.cover_image?.lowUrl ||
          (typeof g.cover_image === 'string' ? g.cover_image : null) ||
          g.images?.[0]?.url ||
          (typeof g.images?.[0] === 'string' ? g.images[0] : null) ||
          g.image?.url ||
          (typeof g.image === 'string' ? g.image : null);

        if (img) {
          const titleParts = formatGameTitle(g.title);
          const genre = g.category?.name || (typeof g.category === 'string' ? g.category : 'Action RPG');
          unique.push({
            id,
            rawGame: g,
            title: g.title || 'Latest Game',
            titlePrefix: titleParts.prefix,
            titleHighlight: titleParts.highlight,
            subtitle: 'LATEST GAMES',
            category: genre,
            genre: genre,
            imageUrl: img,
            image: img,
            rating: g.rating ? Number(g.rating).toFixed(1) : '4.8',
            price: getGamePrice(g),
            priceNum: typeof g.price === 'number' ? g.price : 59.99,
            platforms: getAvailablePlatforms(g),
            description: g.description
              ? (g.description.length > 160 ? g.description.slice(0, 160) + '...' : g.description)
              : 'Embark on an epic adventure with stunning visuals, legendary battles, and immersive gameplay.',
            searchTerm: g.title,
          });
        }
      }
      if (unique.length >= 16) break;
    }

    let result = [...unique];
    if (result.length >= 1 && result.length < 16) {
      const base = [...result];
      let dupIndex = 1;
      while (result.length < 16) {
        for (const item of base) {
          result.push({
            ...item,
            id: `${item.id || 'game'}-dup-${dupIndex}`,
          });
          if (result.length >= 16) break;
        }
        dupIndex++;
      }
    }

    return result;
  }, [newRelease, trending, topSelling, mostPlayed, upcoming]);

  const [activeIndex, setActiveIndex] = useState(() => {
    try {
      const saved = sessionStorage.getItem('hero_active_index');
      if (saved !== null) {
        const parsed = parseInt(saved, 10);
        return !isNaN(parsed) && parsed >= 0 ? parsed : 0;
      }
    } catch (e) {}
    return 0;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem('hero_active_index', activeIndex.toString());
    } catch (e) {
      // Ignore storage errors
    }
  }, [activeIndex]);
  const [isHovered, setIsHovered] = useState(false);
  const [isHeartPopping, setIsHeartPopping] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 390
  );

  // ── Mouse Drag / Swipe & Wheel Navigation State ──
  const carouselDeckRef = useRef(null);
  const prevOffsetsRef = useRef({});
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragStartTime = useRef(0);
  const hasDragged = useRef(false);
  const isVerticalScroll = useRef(false);

  const totalCards = cards.length;
  const activeModulo = totalCards > 0 ? ((activeIndex % totalCards) + totalCards) % totalCards : 0;
  const activeFeatured = totalCards > 0 ? (cards[activeModulo] || cards[0]) : null;

  useEffect(() => {
    prevOffsetsRef.current = {};
  }, [totalCards]);

  const userWishlist = useSelector((state) => state.userSlice?.wishlist || []);
  const isWishlisted = useMemo(() => {
    if (!activeFeatured) return false;
    const realGame = activeFeatured.rawGame || activeFeatured;
    const targetId = realGame._id || realGame.id || activeFeatured.id;
    const cleanId = typeof targetId === 'string' && targetId.includes('-dup-')
      ? targetId.split('-dup-')[0]
      : targetId;

    return userWishlist.some((w) => {
      const wId = w?._id || w?.id || w;
      return String(wId) === String(cleanId) || (realGame._id && String(wId) === String(realGame._id));
    });
  }, [userWishlist, activeFeatured]);

  const nextSlide = useCallback(() => {
    if (totalCards > 0) {
      setActiveIndex((prev) => prev + 1);
    }
  }, [totalCards]);

  const prevSlide = useCallback(() => {
    if (totalCards > 0) {
      setActiveIndex((prev) => prev - 1);
    }
  }, [totalCards]);

  // Auto-play timer (3s) synchronized for both featured showcase and 3D fanner
  useEffect(() => {
    if (isHovered || isDragging || totalCards <= 1) return;
    const timer = setInterval(() => {
      nextSlide();
    }, 3000);
    return () => clearInterval(timer);
  }, [isHovered, isDragging, nextSlide, totalCards]);

  const handleViewGame = (e) => {
    e?.stopPropagation();
    if (!activeFeatured) return;
    if (activeFeatured.rawGame) {
      navigate(getGamePath(activeFeatured.rawGame));
    } else {
      navigate(`/all-games?search=${encodeURIComponent(activeFeatured.searchTerm || activeFeatured.title)}`);
    }
  };

  const handleWishlistToggle = (e) => {
    e?.stopPropagation();
    if (!activeFeatured) return;
    setIsHeartPopping(true);
    setTimeout(() => setIsHeartPopping(false), 500);
    const targetGame = activeFeatured.rawGame || activeFeatured;
    dispatch(toggleWishlist(targetGame));
  };

  // Responsive window resize listener with initial mount sync
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard navigation for carousel and showcase
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') {
        prevSlide();
      }
      if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevSlide, nextSlide]);

  // Touch swipe support on main section (for swiping outside the carousel deck)
  const touchStartX = useRef(0);
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 45) nextSlide();
    if (diff < -45) prevSlide();
  };

  // ── Mouse Wheel Continuous Scroll on 3D Card Deck (wraps circularly, permits vertical page scroll) ──
  useEffect(() => {
    const el = carouselDeckRef.current;
    if (!el) return;

    let wheelDelta = 0;
    let wheelTimer = null;

    const onWheel = (e) => {
      // Only capture when horizontal wheel intent dominates; allow normal vertical page scrolling
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
        return;
      }

      e.preventDefault();
      wheelDelta += e.deltaX;
      const threshold = 35;

      if (Math.abs(wheelDelta) >= threshold) {
        if (wheelDelta > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
        wheelDelta = 0;
      }

      if (wheelTimer) clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        wheelDelta = 0;
      }, 150);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelTimer) clearTimeout(wheelTimer);
    };
  }, [nextSlide, prevSlide]);

  // ── Mouse Drag Handlers (smooth real-time drag feedback on desktop) ──
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartTime.current = Date.now();
    hasDragged.current = false;
    setDragOffset(0);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartX.current;
      if (Math.abs(deltaX) > 6) {
        hasDragged.current = true;
      }
      setDragOffset(deltaX);
    };

    const handleMouseUp = (e) => {
      const deltaX = e.clientX - dragStartX.current;
      const deltaTime = Date.now() - dragStartTime.current;
      setIsDragging(false);
      setDragOffset(0);

      const { stepX } = getDeckGeometry(windowWidth);

      const isFastSwipe = deltaTime < 300 && Math.abs(deltaX) > 25;
      const isDistanceSwipe = Math.abs(deltaX) > stepX * 0.22;

      if (isFastSwipe || isDistanceSwipe) {
        const cardsToAdvance = Math.round(deltaX / stepX);
        if (cardsToAdvance !== 0) {
          setActiveIndex((prev) => prev - cardsToAdvance);
        } else if (deltaX < 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      setTimeout(() => {
        hasDragged.current = false;
      }, 50);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, nextSlide, prevSlide, totalCards, windowWidth]);

  // ── Touch drag handlers for the carousel deck (iPhone & Mobile optimized) ──
  const handleTouchStartDeck = (e) => {
    if (!e.touches?.[0]) return;
    e.stopPropagation();
    setIsDragging(true);
    dragStartX.current = e.touches[0].clientX;
    dragStartY.current = e.touches[0].clientY;
    dragStartTime.current = Date.now();
    hasDragged.current = false;
    isVerticalScroll.current = false;
    setDragOffset(0);
  };

  const handleTouchMoveDeck = (e) => {
    if (!isDragging || !e.touches?.[0]) return;
    const deltaX = e.touches[0].clientX - dragStartX.current;
    const deltaY = e.touches[0].clientY - dragStartY.current;

    // Detect if user intended to scroll vertically down the page on iPhone
    if (!hasDragged.current && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
      isVerticalScroll.current = true;
      setIsDragging(false);
      setDragOffset(0);
      return;
    }

    if (isVerticalScroll.current) return;

    if (Math.abs(deltaX) > 6) {
      hasDragged.current = true;
    }
    setDragOffset(deltaX);
  };

  const handleTouchEndDeck = (e) => {
    if (!isDragging) return;
    e.stopPropagation();
    const deltaX = (e.changedTouches?.[0]?.clientX || dragStartX.current) - dragStartX.current;
    const deltaTime = Date.now() - dragStartTime.current;
    setIsDragging(false);
    setDragOffset(0);

    if (isVerticalScroll.current) {
      isVerticalScroll.current = false;
      return;
    }

    const { stepX } = getDeckGeometry(windowWidth);

    const isFastSwipe = deltaTime < 300 && Math.abs(deltaX) > 25;
    const isDistanceSwipe = Math.abs(deltaX) > stepX * 0.22;

    if (isFastSwipe || isDistanceSwipe) {
      const cardsToAdvance = Math.round(deltaX / stepX);
      if (cardsToAdvance !== 0) {
        setActiveIndex((prev) => prev - cardsToAdvance);
      } else if (deltaX < 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    setTimeout(() => {
      hasDragged.current = false;
    }, 50);
  };

  // Card click handler
  const handleCardClick = (index, card) => {
    if (hasDragged.current) return;
    const currentModulo = totalCards > 0 ? ((activeIndex % totalCards) + totalCards) % totalCards : 0;
    if (index === currentModulo) {
      if (card.rawGame) {
        navigate(getGamePath(card.rawGame));
      }
      return;
    }

    // Calculate the shortest circular offset from current center
    const rawOffset = index - currentModulo;
    const half = totalCards / 2;
    const effectiveOffset = ((((rawOffset + half) % totalCards) + totalCards) % totalCards) - half;

    const targetOffset = Math.round(effectiveOffset);
    setActiveIndex((prev) => prev + targetOffset);
  };


  const getDeckGeometry = (width) => {
    const isMobile = width < 640;
    const isTablet = width >= 640 && width < 1024;
    const is2XL = width >= 1536;

    if (isMobile) {
      // Mobile (< 640px)
      const angleStepDeg = 5.2;
      const radius = 820;
      const yOffset = 8;
      const stepX = radius * Math.sin((angleStepDeg * Math.PI) / 180);
      return { angleStepDeg, radius, yOffset, stepX, isMobile, isTablet };
    }

    if (isTablet) {
      // Tablet (640px - 1023px)
      const angleStepDeg = 6.0;
      const radius = 1250;
      const yOffset = 14;
      const stepX = radius * Math.sin((angleStepDeg * Math.PI) / 180);
      return { angleStepDeg, radius, yOffset, stepX, isMobile, isTablet };
    }

    if (is2XL) {
      // 2XL Desktop (>= 1536px)
      const angleStepDeg = 6.2;
      const radius = 1920;
      const yOffset = 22;
      const stepX = radius * Math.sin((angleStepDeg * Math.PI) / 180);
      return { angleStepDeg, radius, yOffset, stepX, isMobile, isTablet };
    }

    // Standard Desktop (1024px - 1535px)
    const angleStepDeg = 5.8;
    const radius = 1800;
    const yOffset = 18;
    const stepX = radius * Math.sin((angleStepDeg * Math.PI) / 180);
    return { angleStepDeg, radius, yOffset, stepX, isMobile, isTablet };
  };


  const getCardStyle = (index) => {
    if (totalCards === 0) return { style: {}, isCenter: false };

    const { angleStepDeg, radius, yOffset, stepX, isMobile } = getDeckGeometry(windowWidth);

    const dragRatio = isDragging && stepX > 0 ? dragOffset / stepX : 0;
    const rawOffset = (index - activeIndex) + dragRatio;

    const half = totalCards / 2;
    const effectiveOffset = ((((rawOffset + half) % totalCards) + totalCards) % totalCards) - half;

    const absOffset = Math.abs(effectiveOffset);
    const isCenter = absOffset < 0.5;

    const thetaDeg = effectiveOffset * angleStepDeg;
    const thetaRad = (thetaDeg * Math.PI) / 180;

    const translateX = radius * Math.sin(thetaRad);
    const translateY = radius * (1 - Math.cos(thetaRad)) - yOffset;
    const rotateZ = thetaDeg;

    const scaleDrop = isMobile ? 0.08 : 0.06;
    const scale = Math.max(0.74, +(1.04 - absOffset * scaleDrop).toFixed(3));

    const zIndex = Math.round(50 - absOffset * 10);

    const fadeLimit = isMobile ? 3.2 : 4.0;
    const isVisible = absOffset < fadeLimit;
    const opacity = isVisible
      ? Math.max(0, +(1 - Math.pow(absOffset / fadeLimit, 1.8)).toFixed(3))
      : 0;

    const prevOffset = prevOffsetsRef.current[index];
    const hasWrapped = prevOffset !== undefined && Math.abs(effectiveOffset - prevOffset) > half;
    prevOffsetsRef.current[index] = effectiveOffset;

    const style = {
      transform: `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0px) rotateZ(${rotateZ.toFixed(2)}deg) scale(${scale})`,
      WebkitTransform: `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0px) rotateZ(${rotateZ.toFixed(2)}deg) scale(${scale})`,
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden',
      zIndex,
      opacity: hasWrapped ? 0 : opacity,
      visibility: (hasWrapped || !isVisible) ? 'hidden' : 'visible',
      pointerEvents: (hasWrapped || !isVisible) ? 'none' : 'auto',
      transition: (isDragging || hasWrapped)
        ? 'none'
        : (!isVisible
          ? 'none'
          : 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease'),
    };

    return { style, isCenter };
  };


  const getSkeletonCardStyle = (offset) => {
    const { angleStepDeg, radius, yOffset, isMobile } = getDeckGeometry(windowWidth);
    const absOffset = Math.abs(offset);

    const thetaDeg = offset * angleStepDeg;
    const thetaRad = (thetaDeg * Math.PI) / 180;

    const translateX = radius * Math.sin(thetaRad);
    const translateY = radius * (1 - Math.cos(thetaRad)) - yOffset;
    const rotateZ = thetaDeg;

    const scaleDrop = isMobile ? 0.08 : 0.06;
    const scale = Math.max(0.74, +(1.04 - absOffset * scaleDrop).toFixed(3));
    const zIndex = Math.round(50 - absOffset * 10);

    const maxVisibleOffset = isMobile ? 2.6 : 3.6;
    const isVisible = absOffset <= maxVisibleOffset;
    const opacity = isVisible
      ? Math.max(0.25, +(1 - absOffset * (isMobile ? 0.22 : 0.16)).toFixed(3))
      : 0;

    return {
      style: {
        transform: `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0px) rotateZ(${rotateZ.toFixed(2)}deg) scale(${scale})`,
        WebkitTransform: `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0px) rotateZ(${rotateZ.toFixed(2)}deg) scale(${scale})`,
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        zIndex,
        opacity,
        visibility: isVisible ? 'visible' : 'hidden',
        pointerEvents: 'none',
      },
      isCenter: offset === 0,
    };
  };

  const isLoadingData = homeLoading || !activeFeatured || totalCards === 0;

  return (
    <section
      id="hero-section"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className="relative w-full min-h-[580px] sm:min-h-[640px] lg:min-h-[690px] xl:min-h-[720px] flex flex-col items-center justify-start overflow-hidden select-none pt-10 sm:pt-14 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-8 bg-transparent"
      style={{
        backgroundColor: 'transparent',
      }}
    >
      {/* ── EMBEDDED STYLES FOR ANIMATIONS ── */}
      <style>{`
        /* Fluid Cosmic Aurora Left & Right */
        @keyframes auroraDriftLeft {
          0%, 100% { transform: translate(0px, 0px) scale(1); opacity: 0.55; }
          50% { transform: translate(45px, 30px) scale(1.12); opacity: 0.78; }
        }
        .animate-aurora-left {
          animation: auroraDriftLeft 14s ease-in-out infinite;
        }

        @keyframes auroraDriftRight {
          0%, 100% { transform: translate(0px, 0px) scale(1); opacity: 0.48; }
          50% { transform: translate(-40px, 25px) scale(1.15); opacity: 0.72; }
        }
        .animate-aurora-right {
          animation: auroraDriftRight 16s ease-in-out infinite;
        }

        @keyframes pulseCenterGlow {
          0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.45; }
          50% { transform: translateX(-50%) scale(1.15); opacity: 0.7; }
        }
        .animate-center-glow {
          animation: pulseCenterGlow 8s ease-in-out infinite;
        }

        @keyframes heroFadeIn {
          from {
            opacity: 0;
            transform: scale(0.97) translateY(8px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-heroFadeIn {
          animation: heroFadeIn 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes textSlideUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-textSlideUp {
          animation: textSlideUp 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes skeletonShimmer {
          0% {
            transform: translateX(-150%);
          }
          100% {
            transform: translateX(150%);
          }
        }
        .animate-skeleton-shimmer {
          animation: skeletonShimmer 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
      `}</style>

      {/* ── CINEMATIC CYBER-COSMIC ANIMATED BACKGROUND ─────────────────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {/* Base Semi-Transparent Obsidian & Deep Space Radial Gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 90% 70% at 50% 25%, rgba(24, 7, 47, 0.45) 0%, rgba(13, 3, 28, 0.35) 45%, rgba(6, 1, 13, 0.15) 80%, transparent 100%)',
          }}
        />

        {/* Majestic Aurora Shimmer Left: Electric Violet */}
        <div className="absolute -top-36 -left-28 w-[450px] sm:w-[600px] lg:w-[750px] h-[350px] sm:h-[450px] bg-gradient-to-br from-[#9333ea]/25 via-[#6b21a8]/15 to-transparent rounded-full blur-3xl will-change-transform animate-aurora-left pointer-events-none" />

        {/* Majestic Aurora Shimmer Right: Neon Cyan */}
        <div className="absolute top-10 -right-28 w-[400px] sm:w-[550px] lg:w-[700px] h-[340px] sm:h-[450px] bg-gradient-to-bl from-[#00f0ff]/20 via-[#0284c7]/15 to-transparent rounded-full blur-3xl will-change-transform animate-aurora-right pointer-events-none" />

        {/* Ambient Center Highlight Glow behind the showcase artwork */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[350px] sm:w-[500px] h-[280px] sm:h-[360px] bg-gradient-to-r from-[#bd00ff]/15 via-[#ec4899]/12 to-[#00f0ff]/12 rounded-full blur-2xl will-change-transform animate-center-glow pointer-events-none" />

        {/* Bottom Ambient Purple & Indigo Cloud Aura */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[550px] sm:w-[850px] lg:w-[1050px] h-[200px] sm:h-[280px] bg-gradient-to-t from-[#6b21a8]/22 via-[#491480]/12 to-transparent rounded-full blur-2xl pointer-events-none" />

        {/* Bottom Vignette Fade into page content */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-transparent via-transparent to-transparent pointer-events-none" />
      </div>

      {!isLoadingData && activeFeatured ? (
        <>
          {/* ── FEATURED GAME SHOWCASE BANNER ── */}
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="relative z-10 w-full max-w-[1360px] mx-auto py-2 sm:py-6 mt-6 sm:mt-8 select-none"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 xl:gap-14 items-center mt-4 sm:mt-6 lg:mt-10">
              {/* ── LEFT COLUMN (DETAILS, BADGES & CONTROLS): Bottom on Mobile/Tablet, Left on Desktop ── */}
              <div className="order-2 lg:order-1 lg:col-span-6 xl:col-span-5 flex flex-col items-start text-left z-10 w-full">
                {/* Top Bar: Glowing Cyber Tag & Slide Counter */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#00f0ff]/15 via-[#a855f7]/15 to-[#bd00ff]/15 border border-cyan-400/35 backdrop-blur-md shadow-[0_0_15px_rgba(0,240,255,0.2)]">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
                    </span>
                    <span className="text-[10px] sm:text-xs font-cyber font-extrabold uppercase tracking-[0.22em] text-cyan-300">
                      {activeFeatured.subtitle || 'LATEST GAMES'}
                    </span>
                  </div>
                </div>

                {/* Main Title: Metallic White Prefix + Luminous Electric Cyan Gradient */}
                <h1
                  key={`title-${activeModulo}`}
                  className="font-cyber mt-2.5 sm:mt-3.5 font-black text-xl sm:text-2xl md:text-3xl lg:text-[28px] xl:text-[34px] leading-[1.12] sm:leading-[1.1] tracking-tight uppercase animate-textSlideUp break-words"
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-slate-100 to-slate-400 mr-2.5 drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]">
                    {activeFeatured.titlePrefix}
                  </span>
                  <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">
                    {activeFeatured.titleHighlight}
                  </span>
                </h1>

                {/* Game Description Paragraph with Cyber Border */}
                <div className="mt-2.5 sm:mt-3.5 relative pl-3 border-l-2 border-purple-500/40 max-w-lg">
                  <p
                    key={`desc-${activeModulo}`}
                    className="text-slate-300/90 text-xs sm:text-sm md:text-[15px] leading-relaxed font-normal animate-textSlideUp line-clamp-3 sm:line-clamp-none"
                  >
                    {activeFeatured.description}
                  </p>
                </div>

                {/* Info Badges (Rating, Genre, Platforms) */}
                <div className="mt-3.5 sm:mt-4 lg:mt-5 flex items-center gap-2 sm:gap-2.5 flex-wrap max-w-full">
                  {/* Rating Badge */}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#140a2c]/85 border border-amber-500/35 backdrop-blur-md shadow-[0_4px_16px_rgba(0,0,0,0.4)] text-xs font-semibold text-amber-400">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    <span className="text-white font-bold">{activeFeatured.rating}</span>
                  </div>

                  {/* Genre Badge */}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#140a2c]/85 border border-purple-500/35 backdrop-blur-md shadow-[0_4px_16px_rgba(0,0,0,0.4)] text-xs font-medium text-purple-200">
                    <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                    <span className="truncate max-w-[130px] sm:max-w-none">{activeFeatured.genre || activeFeatured.category}</span>
                  </div>

                  {/* Dynamic Game-Wise Platform Badges */}
                  {(() => {
                    const platforms = (activeFeatured.platforms && activeFeatured.platforms.length > 0)
                      ? activeFeatured.platforms
                      : getAvailablePlatforms(activeFeatured.rawGame);

                    if (!platforms || platforms.length === 0) return null;

                    return (
                      <div
                        key={`platforms-${activeFeatured.id || activeIndex}`}
                        className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-[#140a2c]/85 border border-purple-500/30 backdrop-blur-md shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
                        title={platforms.map((p) => PlatformLabel[p] || p).join(', ')}
                      >
                        {platforms.map((plat) => {
                          const Icon = PlatformIcon[plat];
                          if (!Icon) return null;
                          const color = HERO_PLATFORM_COLORS[plat] || PlatformColor[plat] || '#38bdf8';
                          const label = PlatformLabel[plat] || plat.toUpperCase();

                          return (
                            <Icon
                              key={plat}
                              className="w-3.5 h-3.5 transition-all duration-300 hover:scale-125 hover:brightness-125 cursor-pointer"
                              style={{ color }}
                              title={label}
                              aria-label={label}
                            />
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Action Buttons: VIEW GAME, WISHLIST */}
                <div className="mt-4 sm:mt-5 flex items-center gap-3 sm:gap-4 flex-wrap">
                  {/* VIEW GAME Button (Rounded Holo Pill) */}
                  <button
                    onClick={handleViewGame}
                    className="relative group/view p-[1.5px] rounded-full transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer shadow-[0_0_20px_rgba(192,38,211,0.35)] hover:shadow-[0_0_36px_rgba(217,70,239,0.75)]"
                  >
                    {/* Outer Neon Magenta/Violet Border Gradient */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#d946ef] via-[#a855f7] to-[#6366f1] opacity-80 group-hover/view:opacity-100 transition-opacity duration-300" />

                    {/* Inner Dark Holo Core */}
                    <div className="relative px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-[#0c051a]/95 hover:bg-[#160833]/95 text-white font-cyber font-bold text-xs sm:text-sm tracking-wider uppercase flex items-center gap-2 sm:gap-2.5 overflow-hidden backdrop-blur-md transition-colors duration-300">
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-fuchsia-400/25 to-transparent -translate-x-full group-hover/view:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />

                      <Eye className="w-4 h-4 text-fuchsia-400 group-hover/view:text-fuchsia-300 group-hover/view:scale-115 transition-all duration-300 drop-shadow-[0_0_8px_rgba(217,70,239,0.8)]" />
                      <span className="group-hover/view:text-fuchsia-100 transition-colors duration-200 tracking-wider">VIEW GAME</span>
                    </div>
                  </button>

                  {/* Wishlist Heart Button */}
                  <button
                    onClick={handleWishlistToggle}
                    aria-label="Toggle Wishlist"
                    className={`relative group/wish p-[1.5px] rounded-full transition-all duration-300 hover:scale-110 active:scale-90 cursor-pointer ${isWishlisted
                      ? 'shadow-[0_0_28px_rgba(236,72,153,0.75)]'
                      : 'shadow-[0_0_15px_rgba(0,0,0,0.5)] hover:shadow-[0_0_24px_rgba(236,72,153,0.45)]'
                      }`}
                  >
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-300 ${isWishlisted
                        ? 'bg-gradient-to-br from-pink-500 via-rose-500 to-fuchsia-500 opacity-100'
                        : 'bg-gradient-to-br from-purple-500/40 via-pink-500/30 to-purple-800/40 group-hover/wish:from-pink-500/80 group-hover/wish:to-purple-500/80'
                        }`}
                    />

                    <div
                      className={`relative w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 ${isWishlisted ? 'bg-[#22071f]/95 text-pink-400' : 'bg-[#0f0720]/95 text-slate-300 group-hover/wish:text-pink-300'
                        }`}
                    >
                      <Heart
                        className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 ${isWishlisted
                          ? 'fill-pink-500 text-pink-400 drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]'
                          : 'group-hover/wish:drop-shadow-[0_0_8px_rgba(236,72,153,0.5)]'
                          } ${isHeartPopping ? 'scale-125 rotate-12' : 'scale-100 group-hover/wish:scale-110'}`}
                      />
                    </div>
                  </button>
                </div>
              </div>

              {/* ── RIGHT COLUMN (LARGE ARTWORK SHOWCASE): Top on Mobile/Tablet, Right on Desktop ── */}
              <div className="order-1 lg:order-2 lg:col-span-6 xl:col-span-7 relative flex items-center justify-center w-full">
                {/* Cyber Ambient Plasma Aura */}
                <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-tr from-[#00f0ff]/20 via-[#a855f7]/30 to-[#ec4899]/25 rounded-[24px] sm:rounded-[36px] blur-2xl sm:blur-3xl opacity-80 pointer-events-none -z-10 animate-pulse duration-1000" />

                {/* Cyber Gaming Chassis Frame */}
                <div
                  className="relative w-full h-[195px] min-[375px]:h-[220px] min-[425px]:h-[250px] sm:h-[310px] md:h-[370px] lg:h-[415px] xl:h-[445px] p-[2px] sm:p-[2.5px] rounded-2xl sm:rounded-3xl lg:rounded-[32px] bg-gradient-to-r from-[#d946ef] via-[#a855f7] to-[#00f0ff] shadow-[0_20px_50px_rgba(0,0,0,0.95),0_0_35px_rgba(168,85,247,0.35)] group transition-all duration-300"
                >
                  {/* Main Viewport Container */}
                  <div
                    className="relative w-full h-full rounded-[14px] sm:rounded-[22px] lg:rounded-[30px] bg-[#0c051a] overflow-hidden"
                  >
                    {/* Game Artwork Images - Seamless crossfade with zoom-in & zero blinking */}
                    {cards.map((card, idx) => {
                      const isActive = idx === activeModulo;
                      const imageSrc = card.image || card.imageUrl;
                      if (!imageSrc) return null;

                      return (
                        <img
                          key={`${card.id || 'artwork'}-${idx}`}
                          src={imageSrc}
                          alt={card.title}
                          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity transition-transform duration-700 ${
                            isActive
                              ? 'opacity-100 scale-100 group-hover:scale-105 z-10'
                              : 'opacity-0 scale-[0.94] z-0 pointer-events-none'
                          }`}
                          style={{
                            transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                          }}
                          loading="eager"
                          decoding="async"
                        />
                      );
                    })}

                    {/* Cyber CRT / Holographic Scanlines Texture */}
                    <div
                      className="absolute inset-0 pointer-events-none opacity-20"
                      style={{
                        backgroundImage: 'linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.4) 50%)',
                        backgroundSize: '100% 4px',
                      }}
                    />

                    {/* Cinematic Vignette Gradients */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07020e]/95 via-transparent to-black/30 pointer-events-none" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#07020e]/60 via-transparent to-transparent pointer-events-none hidden lg:block" />

                    {/* Laser Light Sweep on Hover */}
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />

                    {/* Accent Dots */}
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[#c084fc] shadow-[0_0_8px_#c084fc,0_0_16px_#a855f7] pointer-events-none z-30" />
                    <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[#c084fc] shadow-[0_0_8px_#c084fc,0_0_16px_#a855f7] pointer-events-none z-30" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── 3D FANNED CARD DECK CAROUSEL ──────────────────────────────────── */}
          <div
            ref={carouselDeckRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStartDeck}
            onTouchMove={handleTouchMoveDeck}
            onTouchEnd={handleTouchEndDeck}
            onTouchCancel={handleTouchEndDeck}
            style={{ touchAction: 'pan-y' }}
            className={`relative z-10 w-full max-w-[1440px] mx-auto flex items-center justify-center h-[145px] sm:h-[170px] md:h-[195px] lg:h-[220px] xl:h-[240px] mt-2 sm:mt-4 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'
              }`}
          >
            <div className="relative flex items-center justify-center w-full h-full mt-0 pointer-events-auto">
              {cards.map((card, index) => {
                const { style, isCenter } = getCardStyle(index);

                return (
                  <div
                    key={card.id ? `${card.id}-${index}` : index}
                    onClick={() => handleCardClick(index, card)}
                    style={style}
                    className="absolute cursor-pointer will-change-transform transform-gpu group select-none"
                  >
                    {/* ── Card Outer Wrapper ── */}
                    <div className="relative">
                      {/* ── Main Card Container (Landscape) ── */}
                      <div
                        className={`relative z-10 w-[128px] h-[80px] sm:w-[158px] sm:h-[98px] md:w-[188px] md:h-[116px] lg:w-[218px] lg:h-[135px] xl:w-[242px] xl:h-[150px] rounded-[14px] sm:rounded-[18px] overflow-hidden bg-[#140822] border transition-all duration-500 ${isCenter
                          ? 'border-purple-400 shadow-[0_0_25px_rgba(168,85,247,0.35)] ring-1 ring-purple-400/50'
                          : 'border-white/10 shadow-[0_8px_22px_rgba(0,0,0,0.85)] hover:border-purple-500/40'
                          }`}
                      >
                        {/* Card Background Image */}
                        <img
                          src={card.imageUrl}
                          alt={card.title}
                          draggable={false}
                          className={`w-full h-full object-cover object-center transition-transform duration-700 ease-out select-none pointer-events-none ${isCenter ? 'scale-105 group-hover:scale-110' : 'group-hover:scale-105'
                            }`}
                          loading="eager"
                        />

                        {/* Dark Vignette Bottom Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent pointer-events-none" />

                        {/* Subtle inner card border highlight */}
                        <div className="absolute inset-0 rounded-[14px] sm:rounded-[18px] ring-1 ring-inset ring-white/15 pointer-events-none" />

                        {/* Non-center card dimmer overlay with smooth opacity transition */}
                        <div
                          className={`absolute inset-0 bg-[#07020e] pointer-events-none transition-opacity duration-500 ${isCenter ? 'opacity-0' : 'opacity-40 group-hover:opacity-10'
                            }`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        /* ── COMPLETE HERO SECTION SKELETON FALLBACK ON INITIAL LOAD ── */
        <>
          {/* Top Banner Grid Skeleton */}
          <div className="relative z-10 w-full max-w-[1360px] mx-auto py-2 sm:py-6 mt-6 sm:mt-8 select-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 xl:gap-14 items-center mt-4 sm:mt-6 lg:mt-10">
              {/* Left Column: Info & Action Skeletons */}
              <div className="order-2 lg:order-1 lg:col-span-6 xl:col-span-5 flex flex-col items-start text-left z-10 w-full space-y-4">
                {/* Category/Tag Pill */}
                <div className="h-6 w-36 rounded-full bg-white/10 border border-cyan-400/20 animate-pulse" />
                {/* Title Lines */}
                <div className="space-y-2 w-full animate-pulse">
                  <div className="h-8 sm:h-10 w-4/5 rounded-xl bg-gradient-to-r from-white/15 via-white/10 to-transparent" />
                  <div className="h-8 sm:h-10 w-3/5 rounded-xl bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-transparent" />
                </div>
                {/* Description Paragraph */}
                <div className="h-16 w-full rounded-xl bg-white/5 border border-white/5 p-3 flex flex-col justify-center animate-pulse">
                  <div className="h-3 w-full rounded bg-white/10 mb-2" />
                  <div className="h-3 w-4/5 rounded bg-white/10 mb-2" />
                  <div className="h-3 w-2/3 rounded bg-white/10" />
                </div>
                {/* Badges (Rating, Genre, Platform) */}
                <div className="flex items-center gap-2.5 flex-wrap animate-pulse">
                  <div className="h-7 w-20 rounded-xl bg-amber-500/10 border border-amber-500/20" />
                  <div className="h-7 w-28 rounded-xl bg-purple-500/10 border border-purple-500/20" />
                  <div className="h-7 w-24 rounded-xl bg-blue-500/10 border border-blue-500/20" />
                </div>
                {/* Action Buttons (View Game & Wishlist) */}
                <div className="flex items-center gap-4 pt-2 animate-pulse">
                  <div className="h-11 w-44 rounded-full bg-gradient-to-r from-purple-600/30 to-cyan-500/30 border border-cyan-400/30" />
                  <div className="h-11 w-11 rounded-full bg-white/10 border border-white/15" />
                </div>
              </div>

              {/* Right Column: Main Artwork Banner Skeleton */}
              <div className="order-1 lg:order-2 lg:col-span-6 xl:col-span-7 flex justify-center w-full">
                <div className="relative w-full aspect-[16/9] sm:aspect-[16/9.5] rounded-2xl sm:rounded-3xl bg-gradient-to-br from-purple-900/20 via-[#0c051f]/80 to-cyan-950/20 border border-purple-500/20 backdrop-blur-xl shadow-[0_12px_45px_rgba(0,0,0,0.85)] flex items-end justify-end p-6 animate-pulse">
                  <div className="h-9 w-28 rounded-xl bg-[#06010d]/90 border border-cyan-400/30" />
                </div>
              </div>
            </div>
          </div>

          {/* ── 3D FANNED CARD DECK CAROUSEL SKELETON (CURVED ARC SAME TO SAME AS IMAGE) ── */}
          <div
            className="relative z-10 w-full max-w-[1440px] mx-auto flex items-center justify-center h-[145px] sm:h-[170px] md:h-[195px] lg:h-[220px] xl:h-[240px] mt-2 sm:mt-4 select-none pointer-events-none"
          >
            <div className="relative flex items-center justify-center w-full h-full mt-0 pointer-events-none">
              {[-3, -2, -1, 0, 1, 2, 3].map((offset) => {
                const { style, isCenter } = getSkeletonCardStyle(offset);

                return (
                  <div
                    key={`hero-skeleton-card-${offset}`}
                    style={style}
                    className="absolute will-change-transform transform-gpu select-none pointer-events-none"
                  >
                    {/* ── Card Outer Wrapper ── */}
                    <div className="relative">
                      {/* ── Main Card Container (Landscape) ── */}
                      <div
                        className={`relative z-10 w-[128px] h-[80px] sm:w-[158px] sm:h-[98px] md:w-[188px] md:h-[116px] lg:w-[218px] lg:h-[135px] xl:w-[242px] xl:h-[150px] rounded-[14px] sm:rounded-[18px] overflow-hidden transition-all duration-500 ${isCenter
                            ? 'border-purple-400/90'
                            : 'border border-white/10'
                          }`}
                      >
                        {/* Shimmer sweep light */}
                        <div className="absolute inset-0 -translate-x-full animate-skeleton-shimmer bg-gradient-to-r from-transparent via-white/[0.08] to-transparent pointer-events-none z-20" />

                        {isCenter ? (
                          /* Center Card (exact layout as center BACKBITE card in image) */
                          <div className="relative w-full h-full flex flex-col items-center justify-center p-3 overflow-hidden">
                            {/* Ambient dark green/purple glow */}
                            <div className="absolute inset-0 bg-gradient-to-b from-[#130722] via-[#091512]/60 to-[#07020e] pointer-events-none" />

                            {/* Vignette Gradients */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />
                            <div className="absolute inset-0 rounded-[14px] sm:rounded-[18px] ring-1 ring-inset ring-purple-400/40 pointer-events-none" />
                          </div>
                        ) : (
                          /* Outer Curved Cards */
                          <div className="relative w-full h-full flex flex-col justify-end p-2.5 sm:p-3 overflow-hidden">
                            {/* Artwork placeholder background */}
                            <div className="absolute inset-0 bg-gradient-to-br from-[#1a0c30] via-[#0d041a] to-[#120722] pointer-events-none" />

                            {/* Artwork center graphic silhouette */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-30">
                              <div className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 rounded-lg bg-white/5 border border-white/5" />
                            </div>

                            {/* Bottom Dark Vignette */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />

                            {/* Dimmer overlay for outer cards */}
                            <div className="absolute inset-0 bg-[#07020e]/40 pointer-events-none" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
}